import {useContext, useEffect, useRef, useState} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { apiUrl } from '../../../../../utils/api_service/api_url';
import { editJobApplicant, searchJobApplicant } from '../../../../../utils/api_service/JobOpenings';
import { useResendPreQualInviteMutation } from '../../../../../utils/api_service/preQualApi';
import { Applicant, ApplicantDocument, JobApplicant } from '../../../../../utils/types/JobOpenings';
import { ICandidateProfile } from '../CandidateProfile';
import {JobOpeningsContext} from "../../../../../utils/contexts/JobOpening";
import {useSelector} from "react-redux";
import {RootState} from "../../../../../utils/redux_store/store";

export const useCandidate = (props: ICandidateProfile) => {
  const { selectedCandidate, handleSendEmailToSingleApplicant } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [applicant, setApplicant] = useState<JobApplicant>();
  const [selectedDocument, setSelectedDocument] = useState<ApplicantDocument | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [optionClick, setOptionClick] = useState<boolean>(true);
  const [leftWidth, setLeftWidth] = useState(47);
  const [activeTab, setActiveTab] =
    useState<
      'overview' | 'skills' | 'experience' | 'education' | 'prequal' | 'notes' | 'documents' | 'cca'
    >('overview');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { id } = useParams<{ id: string }>();

  // Pre-Qual Resend Mutation
  const [resendPreQualInvite, { isLoading: isResending }] = useResendPreQualInviteMutation();
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  const handleResendInvite = async (jobApplicantId: string) => {
    try {
      await resendPreQualInvite(jobApplicantId).unwrap();
      toast.success('Pre-qualifying invitation resent successfully', {
        position: 'bottom-center',
      });
    } catch (error) {
      toast.error('Failed to resend pre-qualifying invitation', {
        position: 'bottom-center',
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const newLeftWidth = (e.clientX / containerWidth) * 100;
      if (newLeftWidth > 20 && newLeftWidth < 80) {
        setLeftWidth(newLeftWidth);
      }
    };

    const stopResizing = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
    };

    const startResizing = () => {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopResizing);
    };

    const resizer = document.getElementById('resizer');
    resizer?.addEventListener('mousedown', startResizing);

    return () => {
      resizer?.removeEventListener('mousedown', startResizing);
      stopResizing();
    };
  }, []);

  const lastRequestedUserId = useRef<string | null>(null);

  const getJobApplicants = async () => {
    if (!selectedCandidate?.userId) return;

    const userIdToFetch = selectedCandidate.userId;
    lastRequestedUserId.current = userIdToFetch;

    setLoading(true);
    setApplicant(undefined); // Clear stale data immediately to prevent showing wrong CV
    setSelectedDocument(null); // Clear stale document metadata immediately
    try {
      const response = await searchJobApplicant(id, userIdToFetch);

      // Prevent race condition: If a newer request was started, ignore this one
      if (lastRequestedUserId.current !== userIdToFetch) return;

      // Handle 404 as normal case (no filters set initially)
      if (response.status === 404) {
        setApplicant(undefined);
        return;
      }

      // Only proceed if we have a successful response
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Check if we still care about this user and have valid data
      if (lastRequestedUserId.current === userIdToFetch && responseData) {
        const transformedData = {
          ...responseData,
          conversationNotes: responseData.conversationNotes?.map(
            (note: any) => ({
              ...note,
              author: note.author
                ? {
                  email: note.author.email,
                  id: note.author.id,
                  firstName: note.author.firstName,
                  lastName: note.author.lastName,
                }
                : null,
            }),
          ),
        };
        setApplicant(transformedData);
      }
    } catch (error) {
      console.error('Error in getJobApplicants:', error);
    } finally {
      if (lastRequestedUserId.current === userIdToFetch) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (applicant?.applicantDocuments) {
      const resume = applicant.applicantDocuments.find((doc: ApplicantDocument) => doc.requirementKey === 'resume');
      if (resume) {
        setSelectedDocument(resume);
      } else if (applicant.applicantDocuments.length > 0) {
        setSelectedDocument(applicant.applicantDocuments[0]);
      }
    }
  }, [applicant]);

  useEffect(() => {
    let active = true;
    let currentBlobUrl: string | null = null;

    const fetchPdf = async () => {
      if (selectedDocument && selectedDocument.requirementKey !== 'resume') {
        setDocLoading(true);
        setDocPreviewUrl(null); // Clear stale URL

        try {
          const url = `${apiUrl}/storage?file_path=${encodeURIComponent(selectedDocument.s3Filename)}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Accept': 'application/json',
            },
          });

          if (response.ok && active) {
            const contentType = response.headers.get('content-type');
            let docUrl = '';

            if (contentType && contentType.includes('application/json')) {
              const data = await response.json();
              docUrl = data.url;
            } else {
              docUrl = response.url;
            }

            if (docUrl && active) {
              const filename = selectedDocument.s3Filename.toLowerCase();
              if (filename.endsWith('.pdf')) {
                // Fetch the actual file content from S3 to create a local blob and force "inline" disposition
                // This prevents the browser from triggering a download based on S3 headers
                try {
                  const s3Response = await fetch(docUrl);
                  if (s3Response.ok && active) {
                    const blob = await s3Response.blob();
                    const previewBlob = new Blob([blob], { type: 'application/pdf' });
                    currentBlobUrl = URL.createObjectURL(previewBlob);
                    setDocPreviewUrl(currentBlobUrl);
                  } else if (active) {
                    setDocPreviewUrl(docUrl); // Fallback to direct URL
                  }
                } catch {
                  if (active) setDocPreviewUrl(docUrl); // Fallback to direct URL on CORS etc
                }
              } else {
                // For non-PDFs (like docx), use the signed URL directly
                setDocPreviewUrl(docUrl);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching document:', error);
        } finally {
          if (active) setDocLoading(false);
        }
      } else {
        setDocPreviewUrl(null);
        setDocLoading(false);
      }
    };

    fetchPdf();

    return () => {
      active = false;
      if (currentBlobUrl) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [selectedDocument, apiUrl]);

  const pdfUrl = (selectedDocument && selectedDocument.requirementKey !== 'resume')
    ? docPreviewUrl || undefined
    : applicant?.pdfUrl;

  useEffect(() => {
    getJobApplicants();
  }, [selectedCandidate, optionClick, id]);

  const isHighlightHit = (word: string): boolean => {
    if (!applicant?.highlightSkills || !word) return false;

    // Normalize string for comparison - lowercase and remove special characters
    const normalizeString = (str: string): string => {
      return str
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s]/g, '') // Keep spaces to split words
        .replace(/\s+/g, ' '); // Normalize multiple spaces to single space
    };

    // Get all words from the input string (handles comma-separated values)
    const wordsInInput = normalizeString(word)
      .split(/[\s,]+/) // Split by spaces and commas
      .filter((w) => w.length > 0);

    // Get all highlighted words (type: "hit") from the highlightSkills array
    const highlightedWords = (applicant.highlightSkills as any)
      .flat()
      .filter((item: any) => item.type === 'hit')
      .map((item: any) => normalizeString(item.value));

    // Check if any word in the input matches any highlighted word
    return wordsInInput.some((inputWord) =>
      highlightedWords.some((highlightWord: string) => {
        // Exact match
        if (inputWord === highlightWord) return true;

        // Partial match (one contains the other)
        if (
          inputWord.includes(highlightWord) ||
          highlightWord.includes(inputWord)
        ) {
          return true;
        }

        // Stem matching for words like "Leadership" and "Leader"
        const minLength = Math.min(inputWord.length, highlightWord.length);
        if (minLength >= 5) {
          // Find common prefix length
          let commonPrefix = 0;
          for (let i = 0; i < minLength; i++) {
            if (inputWord[i] === highlightWord[i]) {
              commonPrefix++;
            } else {
              break;
            }
          }

          // If they share at least 70% of the shorter word as prefix
          if (commonPrefix / minLength >= 0.7) {
            return true;
          }
        }

        return false;
      }),
    );
  };

  // Effect for handling clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleButtonClick = () => setIsOpen(!isOpen);

  const handleOptionClick = async (option: string) => {
    if (option === 'Email & Close' || option === 'Email Only') {
      if (handleSendEmailToSingleApplicant) {
        await handleSendEmailToSingleApplicant(
            selectedCandidate,
            option === 'Email & Close',
        );
      }
      setIsOpen(false);
      return;
    }

    const optionClicked =
        option === 'Unsuccessful' ? 'Unsuccessful-Pending' : option;

    try {
      const response = await editJobApplicant(id, selectedCandidate.userId, {
        applicantStatus: optionClicked,
      });
      if (!response.ok) {
        toast.error('Error changing applicant status', {
          position: 'bottom-center',
        });
        setIsOpen(false);
        return;
      }
      // The backend promotes Pending to Closed and sends the rejection email
      // when this recruiter has auto-send on for the opening, so report the
      // status it actually settled on rather than the one we asked for.
      const updated = await response.json().catch(() => null);
      const finalStatus = updated?.applicantStatus || optionClicked;
      const statusLabel =
        finalStatus === 'Unsuccessful-Closed'
          ? 'Unsuccessful (email sent)'
          : finalStatus === 'Unsuccessful-Pending'
            ? 'Unsuccessful (Pending)'
            : option;
      toast.success(
          `${selectedCandidate.firstName} ${selectedCandidate.lastName} moved to ${statusLabel}`,
          { position: 'bottom-center' },
      );
      setOptionClick(!optionClick);
    } catch {
      toast.error('Error changing applicant status', {
        position: 'bottom-center',
      });
    }
    setIsOpen(false);
  };

  const options = [
    'New',
    'Shortlisted',
    'Suitable',
    'Outstanding',
    'Auto-Assessment',
    'Unsuccessful', // Show as 'Unsuccessful' but handle as 'Unsuccessful-Pending' in logic
    ...(jobOpeningElements?.includes('Psychometric')
        ? ['Psychometric']
        : []),
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
    ...(jobOpeningElements?.includes('Agentic Assessment') ? ['Agentic-Assessment'] : []),
  ];

  if (
    selectedCandidate?.applicantStatus?.includes('Unsuccessful') &&
    handleSendEmailToSingleApplicant
  ) {
    options.push('Email & Close');
    options.push('Email Only');
  }
  return {
    isOpen,
    applicant,
    loading,
    optionClick,
    leftWidth,
    activeTab,
    containerRef,
    dropdownRef,
    handleOptionClick,
    handleButtonClick,
    options,
    isHighlightHit,
    setActiveTab,
    getJobApplicants,
    selectedDocument,
    setSelectedDocument,
    pdfUrl,
    docLoading,
    handleResendInvite,
    isResending,
    id,
  };
};
